<template>
  <div class="tenant-accounts-wrap">
    <h1>{{ $t('settings.tenantAccounts') }}</h1>

    <el-row class="action-row">
      <el-col :span="12">
        <el-input
          class="table-search"
          v-model="searchTerm"
          :placeholder="$t('settings.account.search')"
          prefix-icon="el-icon-search"
          clearable
          @clear="clearSearch"
          @input="debouncedSearch">
        </el-input>
      </el-col>
      <el-col :span="12">
        <div class="account-actions">
          <base-button
            type="primary"
            icon="el-icon-refresh"
            :loading="loading"
            @click="loadAccounts">
            {{ $t('settings.refresh') }}
          </base-button>
          <base-button
            type="info"
            icon="el-icon-back"
            @click="$router.push({path: '/app/tenants'})">
            {{ $t('settings.backToTenants') }}
          </base-button>
        </div>
      </el-col>
    </el-row>

    <base-table
      :loading="loading"
      :data="accounts"
      :empty-text="$t('settings.accountsTableEmpty')"
      stripe
      border
      class="tenant-account-table">
      <el-table-column
        prop="appLoginName"
        width="200"
        :label="$t('base.appLoginName')">
      </el-table-column>
      <el-table-column prop="firstName" :label="$t('base.firstName')">
      </el-table-column>
      <el-table-column prop="lastName" :label="$t('base.lastName')">
      </el-table-column>
      <el-table-column prop="email" min-width="300" :label="$t('base.email')">
      </el-table-column>
      <el-table-column
        min-width="70"
        align="center"
        :label="$t('settings.authority')">
        <template v-slot="account">
          <el-tag v-if="account.row.userAuthority === 'SUPER_ADMIN'" type="danger">Super Admin</el-tag>
          <el-tag v-if="account.row.userAuthority === 'ADMIN'">Admin</el-tag>
          <el-tag v-if="account.row.userAuthority === 'REPORTER'" type="info">Reporter</el-tag>
          <el-tag v-if="account.row.userAuthority === 'USER'" type="info">User</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        align="center"
        :label="$t('settings.active')">
        <template v-slot="account">
          <el-switch
            v-model="account.row.active"
            @change="toggleAccountActive(account.row)">
          </el-switch>
        </template>
      </el-table-column>
      <el-table-column
        fixed="right"
        align="center"
        min-width="300"
        :label="$t('settings.operations')">
        <template v-slot="account">
          <el-tooltip :content="$t('settings.account.sendEmail')" placement="top">
            <el-button
              :disabled="!account.row.email"
              size="small"
              type="primary"
              circle
              icon="el-icon-message"
              @click="sendTestEmail(account.row)">
            </el-button>
          </el-tooltip>
          <el-tooltip :content="$t('settings.account.sendPush')" placement="top">
            <el-button
              :disabled="!account.row.hasPushToken"
              circle
              type="primary"
              size="small"
              icon="el-icon-mobile-phone"
              @click="sendTestPush(account.row)">
            </el-button>
          </el-tooltip>
          <el-tooltip
            :content="$t('settings.account.resetPushToken')"
            placement="top">
            <el-button
              :disabled="!account.row.hasPushToken"
              circle
              type="warning"
              size="small"
              icon="el-icon-remove"
              @click="resetPushToken(account.row)">
            </el-button>
          </el-tooltip>
          <el-tooltip
            :content="$t('settings.edit')"
            placement="top">
            <el-button
              type="primary"
              size="small"
              icon="el-icon-edit"
              @click="editAccount(account.row)"
              circle>
            </el-button>
          </el-tooltip>
          <el-tooltip
            :content="$t('settings.account.delete')"
            placement="top">
            <el-button
              circle
              type="danger"
              size="small"
              icon="el-icon-delete"
              @click="showDeleteAccountDialog(account.row)">
            </el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </base-table>

    <el-pagination
      v-if="pagination.total > 0"
      class="accounts-pagination"
      @size-change="handlePageSizeChange"
      @current-change="handlePageChange"
      :current-page.sync="pagination.page"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pagination.pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="pagination.total">
    </el-pagination>

    <el-dialog
      :title="$t('settings.editAccount')"
      :visible.sync="editAccountDialog"
      width="50%">
      <account-form
        v-if="editAccountDialog && selectedAccount"
        :tenantId="$route.params.id"
        :account="selectedAccount"
        :isEdit="true"
        @submit="onAccountEdit"
        @cancel="editAccountDialog = false"
        :loading="formLoading">
      </account-form>
    </el-dialog>

    <base-delete-dialog
      :visible.sync="deleteAccountDialog"
      :title="$t('settings.deleteAccount')"
      :text="$t('settings.deleteAccountInfoText')"
      :confirm-label="$t('settings.deleteAccount')"
      :loading="loading"
      @confirm="confirmDeleteAccount">
    </base-delete-dialog>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import AccountForm from './AccountForm';
import BaseTable from '@/components/common/BaseTable';
import BaseButton from '@/components/common/BaseButton';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';

const tenantStateMappers = createNamespacedHelpers('tenant');

export default {
  name: "TenantAccounts",
  components: {AccountForm, BaseTable, BaseButton, BaseDeleteDialog},
  data() {
    return {
      accounts: [],
      searchTerm: '',
      loading: false,
      formLoading: false,
      editAccountDialog: false,
      deleteAccountDialog: false,
      selectedAccount: null,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      },
      searchTimeout: null
    }
  },
  mounted() {
    this.loadAccounts();
  },
  methods: {
    ...tenantStateMappers.mapActions([
      'getTenantAccounts',
      'setAccountActive',
      'updateAccount',
      'deleteAccount',
      'testMailAccount',
      'testPushAccount',
      'deleteAccountTokens'
    ]),
    loadAccounts() {
      this.loading = true;

      const filterParams = {
        search: this.searchTerm || undefined
      };

      const pageParams = {
        page: this.pagination.page - 1,
        pageSize: this.pagination.pageSize,
        pageSortDirection: 'ASC'
      };

      this.getTenantAccounts({
        tenantId: this.$route.params.id,
        filterParams,
        pageParams
      }).then(response => {
        if (response.content) {
          this.accounts = response.content;
          this.pagination.total = response.totalElements;
        } else if (Array.isArray(response)) {
          this.accounts = response;
          this.pagination.total = response.length;
        } else {
          this.accounts = [];
          this.pagination.total = 0;
        }
      }).catch(() => {
        this.$message.error(this.$t('settings.genericHttpError'));
      }).finally(() => {
        this.loading = false;
      });
    },
    handlePageSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.loadAccounts();
    },
    handlePageChange() {
      this.loadAccounts();
    },
    clearSearch() {
      this.searchTerm = '';
      this.pagination.page = 1;
      this.loadAccounts();
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadAccounts();
      }, 300);
    },
    toggleAccountActive(account) {
      this.loading = true;
      this.setAccountActive({
        tenantId: this.$route.params.id,
        accountId: account.id,
        isActive: account.active
      }).then(() => {
        this.$message.success(
          account.active
            ? this.$t('settings.account.activated')
            : this.$t('settings.account.deactivated')
        );
      }).catch(() => {
        this.$message.error(this.$t('settings.genericHttpError'));
        account.active = !account.active;
      }).finally(() => {
        this.loading = false;
      });
    },
    editAccount(account) {
      this.selectedAccount = {...account};
      this.editAccountDialog = true;
    },
    onAccountEdit(account) {
      this.formLoading = true;
      this.updateAccount({
        tenantId: this.$route.params.id,
        accountId: account.id,
        account
      }).then(() => {
        this.$message.success(this.$t('settings.accountUpdated'));
        this.editAccountDialog = false;
        this.loadAccounts();
      }).catch(() => {
        this.$message.error(this.$t('settings.genericHttpError'));
      }).finally(() => {
        this.formLoading = false;
      });
    },
    showDeleteAccountDialog(account) {
      this.selectedAccount = account;
      this.deleteAccountDialog = true;
    },
    confirmDeleteAccount() {
      if (this.selectedAccount) {
        this.loading = true;
        this.deleteAccount({
          tenantId: this.$route.params.id,
          accountId: this.selectedAccount.id
        }).then(() => {
          this.$message.success(this.$t('settings.accountDeleted'));
          this.deleteAccountDialog = false;
          this.selectedAccount = null;
          this.loadAccounts();
        }).catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    sendTestEmail(account) {
      this.loading = true;
      this.testMailAccount(account.id).then(() => {
        this.$message.success(this.$t('settings.testEmailSent'));
      }).catch(() => {
        this.$message.error(this.$t('settings.testEmailFailed'));
      }).finally(() => {
        this.loading = false;
      });
    },
    sendTestPush(account) {
      this.loading = true;
      this.testPushAccount(account.id).then(() => {
        this.$message.success(this.$t('settings.testPushSent'));
      }).catch(() => {
        this.$message.error(this.$t('settings.testPushFailed'));
      }).finally(() => {
        this.loading = false;
      });
    },
    resetPushToken(account) {
      this.loading = true;
      this.deleteAccountTokens({
        tenantId: this.$route.params.id,
        accountId: account.id
      }).then(() => {
        this.$message.success(this.$t('settings.pushTokensReset'));
        this.loadAccounts();
      }).catch(() => {
        this.$message.error(this.$t('settings.pushTokensResetFailed'));
      }).finally(() => {
        this.loading = false;
      });
    }
  }
}
</script>

<style scoped lang="scss">
.tenant-accounts-wrap {
  min-width: 0;

  .action-row {
    margin-bottom: 20px;
  }

  .table-search {
    max-width: 400px;
    margin-bottom: 10px;
  }

  .account-actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
  }

  .tenant-account-table {
    width: 100%;
    margin-bottom: 15px;
  }

  .accounts-pagination {
    margin-top: 20px;
    text-align: center;
    padding: 10px 0;
  }
}
</style>
